import React, { useState, useEffect } from 'react';
import { FiUser, FiMail, FiClock, FiCheck, FiX, FiEye, FiImage, FiChevronDown, FiChevronUp, FiDownload, FiMessageSquare, FiTrash2, FiFlag } from 'react-icons/fi';
import { buildVariantUrl } from '../utils/photoCache';

export default function ClientSubmissions({ shareId, onClose }) {
  const [activeTab, setActiveTab] = useState('submissions'); // submissions | comments
  const [submissions, setSubmissions] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [submissionDetail, setSubmissionDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (shareId) {
      loadSubmissions();
      loadComments();
    }
  }, [shareId]);

  async function loadSubmissions() {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`/api/shares/${shareId}/submissions`, { credentials: 'include' });
      if (!res.ok) {
        throw new Error('Failed to load submissions');
      }
      const data = await res.json();
      setSubmissions(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function toggleExpand(id) {
    if (expandedId === id) {
      setExpandedId(null);
      setSubmissionDetail(null);
      return;
    }

    setExpandedId(id);
    setLoadingDetail(true);

    try {
      const res = await fetch(`/api/shares/submissions/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSubmissionDetail(data);
      }
    } catch (err) {
      console.error('Failed to load submission detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  }

  async function updateStatus(id, status) {
    try {
      const res = await fetch(`/api/shares/submissions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });

      if (res.ok) {
        // Update local state
        setSubmissions(prev => prev.map(sub =>
          sub.id === id ? { ...sub, status } : sub
        ));
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  }

  function downloadSubmission(id) {
    window.open(`/api/shares/submissions/${id}/download`, '_blank');
  }

  async function loadComments() {
    try {
      const res = await fetch(`/api/shares/${shareId}/comments`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setComments(data);
      }
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  }

  async function updateCommentStatus(id, status) {
    try {
      const res = await fetch(`/api/shares/comments/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });

      if (res.ok) {
        setComments(prev => prev.map(c =>
          c.id === id ? { ...c, status } : c
        ));
      }
    } catch (err) {
      console.error('Failed to update comment status:', err);
    }
  }

  async function deleteComment(id) {
    if (!confirm('Delete this comment?')) return;

    try {
      const res = await fetch(`/api/shares/comments/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setComments(prev => prev.filter(c => c.id !== id));
      }
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  }

  function getCommentStatusColor(status) {
    switch (status) {
      case 'pending': return '#3e5c58';
      case 'approved': return '#22c55e';
      case 'rejected': return '#ef4444';
      case 'flagged': return '#3e5c58';
      default: return '#435654';
    }
  }

  function getStatusColor(status) {
    switch (status) {
      case 'submitted': return '#304849';
      case 'reviewed': return '#3e5c58';
      case 'approved': return '#22c55e';
      case 'rejected': return '#ef4444';
      default: return '#435654';
    }
  }

  function formatDate(dateStr) {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function getImageUrl(filename) {
    if (!filename) return null;
    return buildVariantUrl({ filename }, 'xl');
  }

  if (loading) {
    return (
      <div className="client-submissions-panel">
        <div className="submissions-header">
          <h3>Client Submissions</h3>
          {onClose && <button onClick={onClose} className="close-btn"><FiX /></button>}
        </div>
        <div className="submissions-loading">Loading submissions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-submissions-panel">
        <div className="submissions-header">
          <h3>Client Submissions</h3>
          {onClose && <button onClick={onClose} className="close-btn"><FiX /></button>}
        </div>
        <div className="submissions-error">{error}</div>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="client-submissions-panel">
        <div className="submissions-header">
          <h3>Client Submissions</h3>
          {onClose && <button onClick={onClose} className="close-btn"><FiX /></button>}
        </div>
        <div className="submissions-empty">
          <FiImage size={32} style={{ opacity: 0.3 }} />
          <p>No submissions yet</p>
          <span>Client selections will appear here</span>
        </div>
      </div>
    );
  }

  return (
    <div className="client-submissions-panel">
      <div className="submissions-header">
        <h3>Client Activity</h3>
        {onClose && <button onClick={onClose} className="close-btn"><FiX /></button>}
      </div>

      <div className="submissions-tabs">
        <button
          className={`tab-btn ${activeTab === 'submissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('submissions')}
        >
          <FiImage size={16} />
          Submissions ({submissions.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'comments' ? 'active' : ''}`}
          onClick={() => setActiveTab('comments')}
        >
          <FiMessageSquare size={16} />
          Comments ({comments.length})
        </button>
      </div>

      {activeTab === 'submissions' && (
      <div className="submissions-list">
        {submissions.length === 0 ? (
          <div className="submissions-empty-inline">
            <FiImage size={24} style={{ opacity: 0.3 }} />
            <span>No submissions yet</span>
          </div>
        ) : submissions.map(sub => (
          <div key={sub.id} className="submission-card">
            <div className="submission-main" onClick={() => toggleExpand(sub.id)}>
              <div className="submission-info">
                <div className="submission-client">
                  <FiUser size={14} />
                  <span>{sub.client_name || 'Anonymous'}</span>
                </div>
                {sub.client_email && (
                  <div className="submission-email">
                    <FiMail size={14} />
                    <span>{sub.client_email}</span>
                  </div>
                )}
                <div className="submission-meta">
                  <span className="submission-date">
                    <FiClock size={12} />
                    {formatDate(sub.submitted_at)}
                  </span>
                  <span className="submission-count">
                    <FiImage size={12} />
                    {sub.photo_count} photos
                  </span>
                </div>
              </div>

              <div className="submission-right">
                <span
                  className="status-badge"
                  style={{ background: getStatusColor(sub.status) + '20', color: getStatusColor(sub.status) }}
                >
                  {sub.status}
                </span>
                {expandedId === sub.id ? <FiChevronUp /> : <FiChevronDown />}
              </div>
            </div>

            {expandedId === sub.id && (
              <div className="submission-expanded">
                {loadingDetail ? (
                  <div className="detail-loading">Loading photos...</div>
                ) : submissionDetail?.items ? (
                  <>
                    <div className="submission-photos">
                      {submissionDetail.items.map(item => (
                        <div key={item.id} className="submission-photo">
                          {item.photo_missing ? (
                            <div className="submission-photo-missing">
                              <span>Removed</span>
                              <em>{item.title || item.filename || 'Photo'}</em>
                            </div>
                          ) : (
                            <img src={getImageUrl(item.filename)} alt={item.title || 'Selected photo'} />
                          )}
                          {item.title && <span className="photo-title">{item.title}</span>}
                        </div>
                      ))}
                    </div>

                    <div className="submission-actions">
                      <button
                        onClick={(e) => { e.stopPropagation(); downloadSubmission(sub.id); }}
                        className="submission-action-btn download"
                      >
                        <FiDownload /> Download
                      </button>
                      {sub.status === 'submitted' && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); updateStatus(sub.id, 'approved'); }}
                            className="submission-action-btn approve"
                          >
                            <FiCheck /> Approve
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); updateStatus(sub.id, 'rejected'); }}
                            className="submission-action-btn reject"
                          >
                            <FiX /> Reject
                          </button>
                        </>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {activeTab === 'comments' && (
        <div className="comments-list">
          {comments.length === 0 ? (
            <div className="submissions-empty-inline">
              <FiMessageSquare size={24} style={{ opacity: 0.3 }} />
              <span>No comments yet</span>
            </div>
          ) : comments.map(comment => (
            <div key={comment.id} className="comment-card">
              <div className="comment-header">
                <div className="comment-author">
                  <FiUser size={14} />
                  <span>{comment.client_name || 'Anonymous'}</span>
                  {comment.client_email && (
                    <span className="comment-email">({comment.client_email})</span>
                  )}
                </div>
                <span
                  className="status-badge"
                  style={{ background: getCommentStatusColor(comment.status) + '20', color: getCommentStatusColor(comment.status) }}
                >
                  {comment.status}
                </span>
              </div>

              {comment.photo_id && (
                <div className="comment-photo-ref">
                  {comment.photo_missing ? (
                    <div className="comment-photo-missing">Removed</div>
                  ) : (
                    <img src={getImageUrl(comment.filename)} alt="Referenced photo" />
                  )}
                  {comment.photo_title && <span>{comment.photo_title}</span>}
                </div>
              )}

              <div className="comment-text">
                {comment.comment}
              </div>

              <div className="comment-footer">
                <span className="comment-date">
                  <FiClock size={12} />
                  {formatDate(comment.created_at)}
                </span>

                <div className="comment-actions">
                  {comment.status === 'pending' && (
                    <>
                      <button
                        onClick={() => updateCommentStatus(comment.id, 'approved')}
                        className="action-btn-sm approve"
                        title="Approve"
                      >
                        <FiCheck size={14} />
                      </button>
                      <button
                        onClick={() => updateCommentStatus(comment.id, 'flagged')}
                        className="action-btn-sm flag"
                        title="Flag"
                      >
                        <FiFlag size={14} />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => deleteComment(comment.id)}
                    className="action-btn-sm delete"
                    title="Delete"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .client-submissions-panel {
          background: var(--bg-primary, #fff);
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .submissions-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.25rem;
          border-bottom: 1px solid var(--border-color, #e0e0e0);
          gap: 1rem;
        }

        .submissions-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          flex: 1;
        }

        .close-btn {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 0.5rem;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
        }

        .close-btn:hover {
          background: var(--bg-secondary, #f5f5f5);
        }

        .submissions-tabs {
          display: flex;
          border-bottom: 1px solid var(--border-color, #e0e0e0);
          padding: 0 1rem;
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border: none;
          background: transparent;
          color: var(--text-secondary, #435654);
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          transition: all 0.2s;
        }

        .tab-btn:hover {
          color: var(--text-primary, #182322);
        }

        .tab-btn.active {
          color: #3e5c58;
          border-bottom-color: #3e5c58;
        }

        .submissions-empty-inline {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          padding: 2rem;
          color: var(--text-secondary, #435654);
          text-align: center;
        }

        .comments-list {
          flex: 1;
          overflow-y: auto;
          min-height: 0;
        }

        .comment-card {
          padding: 1rem 1.25rem;
          border-bottom: 1px solid var(--border-color, #e0e0e0);
        }

        .comment-card:last-child {
          border-bottom: none;
        }

        .comment-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .comment-author {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-weight: 500;
          font-size: 0.9rem;
          color: var(--text-primary, #182322);
        }

        .comment-email {
          font-weight: 400;
          color: var(--text-secondary, #435654);
          font-size: 0.8rem;
        }

        .comment-photo-ref {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          background: var(--bg-secondary, #f5f5f5);
          border-radius: 6px;
          margin-bottom: 0.5rem;
        }

        .comment-photo-ref img {
          width: 40px;
          height: 40px;
          object-fit: cover;
          border-radius: 4px;
        }

        .comment-photo-missing {
          width: 40px;
          height: 40px;
          border-radius: 4px;
          background: var(--bg-secondary, #f5f5f5);
          border: 1px dashed var(--border-color, #e0e0e0);
          font-size: 0.65rem;
          color: var(--text-secondary, #435654);
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0.25rem;
        }

        .comment-photo-ref span {
          font-size: 0.8rem;
          color: var(--text-secondary, #435654);
        }

        .comment-text {
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--text-primary, #182322);
          white-space: pre-wrap;
          word-break: break-word;
        }

        .comment-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.75rem;
        }

        .comment-date {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.8rem;
          color: var(--text-secondary, #435654);
        }

        .comment-actions {
          display: flex;
          gap: 0.25rem;
        }

        .action-btn-sm {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn-sm.approve {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        }

        .action-btn-sm.approve:hover {
          background: #22c55e;
          color: white;
        }

        .action-btn-sm.flag {
          background: rgba(62, 92, 88, 0.1);
          color: #3e5c58;
        }

        .action-btn-sm.flag:hover {
          background: #3e5c58;
          color: white;
        }

        .action-btn-sm.delete {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .action-btn-sm.delete:hover {
          background: #ef4444;
          color: white;
        }

        .submissions-loading,
        .submissions-error {
          padding: 2rem;
          text-align: center;
          color: var(--text-secondary);
        }

        .submissions-error {
          color: var(--danger-color, #ef4444);
        }

        .submissions-empty {
          padding: 3rem 2rem;
          text-align: center;
          color: var(--text-secondary);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 200px;
        }

        .submissions-empty p {
          margin: 1rem 0 0.25rem;
          font-weight: 500;
          color: var(--text-primary);
        }

        .submissions-empty span {
          font-size: 0.9rem;
        }

        .submissions-list {
          flex: 1;
          overflow-y: auto;
          min-height: 0;
        }

        .submission-card {
          border-bottom: 1px solid var(--border-color, #e0e0e0);
        }

        .submission-card:last-child {
          border-bottom: none;
        }

        .submission-main {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.25rem;
          cursor: pointer;
          transition: background 0.2s;
        }

        .submission-main:hover {
          background: var(--bg-secondary, #f5f5f5);
        }

        .submission-info {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }

        .submission-client,
        .submission-email {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.9rem;
        }

        .submission-client {
          font-weight: 500;
          color: var(--text-primary);
        }

        .submission-email {
          color: var(--text-secondary);
          font-size: 0.85rem;
        }

        .submission-meta {
          display: flex;
          gap: 1rem;
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .submission-date,
        .submission-count {
          display: flex;
          align-items: center;
          gap: 0.3rem;
        }

        .submission-right {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .status-badge {
          padding: 0.25rem 0.6rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .submission-expanded {
          padding: 0 1.25rem 1.25rem;
        }

        .detail-loading {
          padding: 1rem;
          text-align: center;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .submission-photos {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .submission-photo {
          position: relative;
        }

        .submission-photo img {
          width: 100%;
          aspect-ratio: 1;
          object-fit: cover;
          border-radius: 6px;
        }

        .submission-photo-missing {
          width: 100%;
          aspect-ratio: 1;
          border-radius: 6px;
          background: var(--bg-secondary, #f5f5f5);
          border: 1px dashed var(--border-color, #e0e0e0);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          color: var(--text-secondary);
          padding: 0.5rem;
          text-align: center;
        }

        .submission-photo-missing em {
          font-style: normal;
          font-size: 0.65rem;
          margin-top: 0.25rem;
        }

        .submission-photo .photo-title {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 0.25rem;
          background: linear-gradient(transparent, rgba(0,0,0,0.7));
          color: white;
          font-size: 0.7rem;
          border-radius: 0 0 6px 6px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .submission-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .submission-action-btn {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.5rem 0.875rem;
          border: none;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .submission-action-btn.approve {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        }

        .submission-action-btn.approve:hover {
          background: #22c55e;
          color: white;
        }

        .submission-action-btn.reject {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .submission-action-btn.reject:hover {
          background: #ef4444;
          color: white;
        }

        .submission-action-btn.review {
          background: rgba(62, 92, 88, 0.1);
          color: #3e5c58;
        }

        .submission-action-btn.review:hover {
          background: #3e5c58;
          color: white;
        }

        .submission-action-btn.download {
          background: rgba(62, 92, 88, 0.1);
          color: #3e5c58;
        }

        .submission-action-btn.download:hover {
          background: #3e5c58;
          color: white;
        }

        /* Mobile optimization */
        @media (max-width: 480px) {
          .client-submissions-panel {
            border-radius: 0;
          }

          .submissions-header {
            padding: 0.875rem 1rem;
          }

          .submissions-header h3 {
            font-size: 0.95rem;
          }

          .submissions-list {
            flex: 1;
            min-height: 0;
          }

          .submission-main {
            padding: 0.875rem 1rem;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .submission-right {
            width: 100%;
            justify-content: space-between;
          }

          .submission-info {
            width: 100%;
          }

          .submission-client {
            font-size: 0.95rem;
          }

          .submission-email {
            font-size: 0.8rem;
          }

          .submission-meta {
            flex-wrap: wrap;
            gap: 0.5rem 1rem;
          }

          .submission-expanded {
            padding: 0 1rem 1rem;
          }

          .submission-photos {
            grid-template-columns: repeat(3, 1fr);
            gap: 0.375rem;
          }

          .submission-actions {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.5rem;
            width: 100%;
          }

          .submission-action-btn {
            justify-content: center;
            padding: 0.625rem 0.5rem;
            font-size: 0.8rem;
          }

          .submission-action-btn.download {
            grid-column: 1 / -1;
          }
        }
      `}</style>
    </div>
  );
}
